import { Component, Input } from '@angular/core';

import { ParticipantMarketType, ParticipantPriceType } from '@cds/betting-offer';
import { Sitecore } from '@frontend/sports/common/client-config-data-access';
import {
    GreyhoundsRaceWinMarketComponentViewModel,
    HorseRaceSortingDirection,
    RacingOptionMarketTypes,
    RacingParticipantWithEventOption,
    RacingParticipantWithParticipantOption,
    WinGameFixture,
    WinGameParticipant,
} from '@frontend/sports/racing/feature/models';
import { trackingConstants } from '@frontend/sports/tracking/feature';

import { DropdownOption } from '../../../../dropdown/models';
import { getDayOfRace } from '../../../../horse-racing-shared/helpers';
import { HorsesFixtureMarketHelper } from '../../../../horse-racing-shared/services/horses-fixture-market-helper.service';
import { HorsesFixtureParticipantHelper } from '../../../../horse-racing-shared/services/horses-fixture-participant-helper.service';
import { HorsesFixturePricingHelper } from '../../../../horse-racing-shared/services/horses-fixture-pricing-helper.service';
import { HorsesFixtureSortingHelper } from '../../../../horse-racing-shared/services/horses-fixture-sorting-helper.service';
import { ParticipantsService } from '../../../common/participant-service';
import { HorsesRaceService } from '../../../services/horses-race.service';
import { RacingService } from '../../../services/racing-service';
import { BaseRaceWinMarketService } from '../../base/base-race-win-market.service';

@Component({ template: '', standalone: false })
export abstract class GhBaseParticipantsComponent {
    @Input() fixture: WinGameFixture;
    @Input() displayRunners = true;
    @Input() hideRunnerInfo = false;
    @Input() trackingSource: string;
    @Input() sortBy: HorseRaceSortingDirection;
    @Input() isNonRunner = false;
    @Input() isNextRaces = false;
    @Input() displayType: string;
    @Input() optionPickTrackingsource: string;
    @Input() marketType: string | undefined;

    expandedParticipant: { [id: number]: boolean } = {};
    isRunnerInfo = false;
    isAllexpanded: boolean;
    activeOption: DropdownOption;
    trackingOptions: { [key: string]: string };
    participants: (RacingParticipantWithEventOption | RacingParticipantWithParticipantOption)[] = [];
    racingOptionMarketTypes = RacingOptionMarketTypes;

    vm: GreyhoundsRaceWinMarketComponentViewModel = {
        sortBy: HorseRaceSortingDirection.CardNumber,
        runners: [],
        nonRunners: [],
        allPricesLocked: false,
        allPricesSP: false,
        expandedParticipant: {},
    };

    priceType = { Fixed: ParticipantPriceType.Fixed, StartingPrice: ParticipantPriceType.StartingPrice };

    constructor(
        protected horsesFixturePricingHelper: HorsesFixturePricingHelper,
        protected racingService: RacingService,
        protected horsesFixtureParticipantHelper: HorsesFixtureParticipantHelper,
        protected horsesFixtureSortingHelper: HorsesFixtureSortingHelper,
        protected horsesFixtureMarketHelper: HorsesFixtureMarketHelper,
        protected horsesRaceService: HorsesRaceService,
        public sitecore: Sitecore,
        protected baseRaceWinMarketService: BaseRaceWinMarketService,
        protected participantsService: ParticipantsService,
    ) {}

    abstract setViewModel(isNewFixture: boolean, fixture: WinGameFixture): void;

    protected setOptions(fixture: WinGameFixture) {
        const options = this.horsesFixturePricingHelper.getPrices(fixture, ParticipantMarketType.Win, ParticipantPriceType.Fixed);
        const spOptions = this.horsesFixturePricingHelper.getPrices(fixture, ParticipantMarketType.Win, ParticipantPriceType.StartingPrice);
        const runners = this.horsesFixtureParticipantHelper.getParticipants(fixture.participants);
        this.vm.allPricesLocked = this.horsesFixturePricingHelper.allPricesAreLocked(fixture, runners, options);
        this.vm.allPricesSP = this.horsesFixturePricingHelper.allPricesAreSP(options, runners);
        return { options, spOptions };
    }

    protected restoreAndFilter(participants: (RacingParticipantWithEventOption | RacingParticipantWithParticipantOption)[]): WinGameParticipant[] {
        const sorted = this.horsesRaceService.restoreParticipantOrder(participants, this.fixture);
        return this.horsesFixtureParticipantHelper.getParticipants(sorted);
    }

    protected setShowInfo(participant: RacingParticipantWithParticipantOption | RacingParticipantWithEventOption): boolean {
        return (
            !!participant.racingPostInfo?.greyhoundsData.spotlightComment ||
            !!participant.racingPostInfo?.greyhoundsData.topSpeed ||
            !!participant.racingPostInfo?.greyhoundsData.starRating
        );
    }

    protected sortNonRunners(fixtures: (RacingParticipantWithParticipantOption | RacingParticipantWithEventOption)[]): string {
        return fixtures
            .sort((a, b) => (a.participantId || 0) - (b.racingPostInfo?.greyhoundsData.trapNumber || 0))
            .map((fp) => `${fp.participantId} ${fp.properties!.greyhound || fp.racingPostInfo?.greyhoundsData.dogName}`)
            .join(', ');
    }

    protected getTrackingOptions(fixture: WinGameFixture, participant: RacingParticipantWithEventOption | RacingParticipantWithParticipantOption) {
        const date = new Date(fixture.startDate);
        const favoriteParticipant = participant.attributes.isFavorite ? '_favourite' : '';

        return {
            [trackingConstants.COMPONENT_MODULE_CUSTOM_NAME]: fixture.meeting.name.value,
            [trackingConstants.COMPONENT_MODULE_NAME]: this.isNextRaces ? 'Greyhound_NextRaces' : 'Greyhound_Racecard',
            [trackingConstants.COMPONENT_MODULE_POSITION]: date.getHours() + ':' + date.getMinutes(),
            [trackingConstants.COMPONENT_MODULE_SOURCE]: '',
            [trackingConstants.COMPONENT_BET_SOURCE]: this.isNextRaces
                ? 'Greyhound_NextRaces/' + 'win each way' + favoriteParticipant
                : 'Greyhound_Racecard/' + this.marketType?.toLowerCase() + favoriteParticipant,
            [trackingConstants.BET_CALENDER]: getDayOfRace(fixture.startDate),
        };
    }

    protected getTrackingSource(
        optionPickTrackingsource: string,
        participant: RacingParticipantWithEventOption | RacingParticipantWithParticipantOption,
    ) {
        optionPickTrackingsource = this.isNextRaces ? optionPickTrackingsource + '/' : optionPickTrackingsource;

        optionPickTrackingsource = this.fetchTrackingSource(this.marketType, optionPickTrackingsource);

        return optionPickTrackingsource + (participant.attributes.isFavorite ? '_favourite' : '');
    }

    trackParticipant(index: number, runner: RacingParticipantWithParticipantOption): number {
        return runner.id;
    }

    protected orderParticipantsBy(sortBy: HorseRaceSortingDirection, fixture: WinGameFixture, isSports: boolean): void {
        this.participants = this.participantsService.orderParticipantsBy(sortBy, this.participants, fixture, isSports);
    }

    fetchTrackingSource(marketType: string | undefined, optionPickTrackingsource: string): string {
        switch (this.marketType) {
            case this.racingOptionMarketTypes.WinEw:
                optionPickTrackingsource = optionPickTrackingsource + 'win each way';
                break;

            case this.racingOptionMarketTypes.InsuranceX:
                optionPickTrackingsource = optionPickTrackingsource + 'moneyback';
                break;

            case this.racingOptionMarketTypes.TopX:
                optionPickTrackingsource = optionPickTrackingsource + 'topfinish';
                break;

            case this.racingOptionMarketTypes.ToFinishX:
                optionPickTrackingsource = optionPickTrackingsource + 'tofinish';
                break;
            default:
                optionPickTrackingsource = optionPickTrackingsource + marketType?.toLowerCase();
        }
        return optionPickTrackingsource;
    }
}
